import { Injectable, signal } from '@angular/core';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'RGCheckInTheme';

/**
 * Persisted light/dark/system preference for the check-in app.
 *
 * The app's M3 colour tokens are flipped purely in CSS (styles.scss) off the
 * `data-theme` attribute on `<html>`:
 *   - `data-theme="light"` -> the base light tokens.
 *   - `data-theme="dark"`  -> the dark token block (manual override).
 *   - `data-theme="system"` -> light by default, dark under
 *     `@media (prefers-color-scheme: dark)`.
 * So this service's only job is to keep that attribute (and localStorage) in
 * sync with the user's choice; no per-component theming runs here.
 *
 * index.html sets the attribute from localStorage BEFORE first paint to avoid a
 * flash; this service re-applies it on boot (it's injected by AppComponent) and
 * on every change.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _mode = signal<ThemeMode>(this.readStored());
  /** The current preference (`system` until the user changes it). */
  readonly mode = this._mode.asReadonly();

  constructor() {
    this.apply(this._mode());
  }

  setMode(mode: ThemeMode): void {
    this._mode.set(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Private-mode / storage-disabled: still apply for this session.
    }
    this.apply(mode);
  }

  private apply(mode: ThemeMode): void {
    document.documentElement.setAttribute('data-theme', mode);
  }

  private readStored(): ThemeMode {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      stored = null;
    }
    return stored === 'light' || stored === 'dark' || stored === 'system'
      ? stored
      : 'system';
  }
}
